/**
 * JacksonSuiteLogin
 *
 * The branded login door for the Jackson family's Member Suite.
 *
 *   Path:       /suite/jackson
 *   Authorized: jarsha30@gmail.com · nadrannejackson@gmail.com
 *               (enforced by access_expires_at + jackson_temp_access on the user record)
 *   Expires:    18 days from delivery (set by the seed script)
 *
 * Anyone else → "Access not found." Same response wording for both
 * "unauthorized email" and "wrong password" so we don't leak which
 * emails are on the allow-list.
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const ALLOWED_EMAILS = ["jarsha30@gmail.com", "nadrannejackson@gmail.com"];

export default function JacksonSuiteLogin() {
  const { user, login, checkSession } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // If already signed in as one of the authorized clients, skip the door.
    if (user?.role === "client" && ALLOWED_EMAILS.includes((user.email || "").toLowerCase())) {
      navigate("/portal/client", { replace: true });
    }
    // Non-client sessions (advisor / owner / dev) are NOT redirected away —
    // the Jackson door must always render for the family. If a stale advisor
    // cookie is present, the client can simply submit their own credentials
    // and the /api/auth/login call will rotate the session.
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");

    const normalizedEmail = email.trim().toLowerCase();
    if (!ALLOWED_EMAILS.includes(normalizedEmail)) {
      // Wait a hair to make the brute-force timing roughly equal.
      await new Promise((r) => setTimeout(r, 350));
      setError("Access not found. Please check the email on your invitation.");
      setBusy(false);
      return;
    }

    try {
      const res = await axios.post(
        `${API}/api/auth/login`,
        { email: normalizedEmail, password },
        { withCredentials: true }
      );
      // Defensive role check — even though the email pre-filter blocks
      // non-Jackson addresses, also reject any non-client role that
      // somehow makes it through (e.g., shared inbox repurposed for an
      // advisor account).  Wave 12.x security separation.
      const role = (res?.data?.role || "").toLowerCase();
      if (role !== "client") {
        try {
          await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
        } catch (_e) { /* noop */ }
        setError("These credentials are not for this door. Please use the studio sign-in.");
        setBusy(false);
        return;
      }
      await checkSession();
      navigate("/portal/client", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail.toLowerCase().includes("suite access has ended")) {
        setError("Your suite access has ended. Please contact Antonia.");
      } else {
        setError("Access not found. Please check your password.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0c0a] text-[#f5f1ea] flex flex-col">
      {/* Header — minimal, no public-site chrome */}
      <header className="px-6 sm:px-12 pt-10 pb-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#c9a96e]">
          VÉRAULT · Member Suite
        </p>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 sm:px-12 pb-16">
        <div
          className="w-full max-w-[420px]"
          data-testid="jackson-suite-login"
        >
          <div className="mb-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8a7a5f] mb-3">
              The Jackson Family
            </p>
            <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] text-[#f5f1ea]">
              Welcome,<br />Jonathan &amp; Nadranne.
            </h1>
            <p className="mt-5 font-accent italic text-[15px] leading-relaxed text-[#b7a98a] max-w-[360px]">
              Your private suite for the twentieth-anniversary journey is
              ready. Sign in with the email and access code from your
              invitation.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" autoComplete="on">
            <div>
              <label
                htmlFor="jackson-email"
                className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[#8a7a5f] mb-2"
              >
                Your email
              </label>
              <input
                id="jackson-email"
                type="email"
                name="username"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                disabled={busy}
                className="w-full px-4 py-3 bg-[#191713] border border-[#3a342a] rounded-md text-[#f5f1ea] placeholder:text-[#5a5346] focus:outline-none focus:border-[#c9a96e] focus:ring-1 focus:ring-[#c9a96e]/40 transition-colors"
                data-testid="jackson-suite-email"
              />
            </div>

            <div>
              <label
                htmlFor="jackson-password"
                className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[#8a7a5f] mb-2"
              >
                Access code
              </label>
              <input
                id="jackson-password"
                type="password"
                name="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                disabled={busy}
                className="w-full px-4 py-3 bg-[#191713] border border-[#3a342a] rounded-md text-[#f5f1ea] placeholder:text-[#5a5346] focus:outline-none focus:border-[#c9a96e] focus:ring-1 focus:ring-[#c9a96e]/40 transition-colors"
                data-testid="jackson-suite-password"
              />
            </div>

            {error && (
              <p
                className="font-accent italic text-[13px] text-[#d4836a] leading-relaxed"
                data-testid="jackson-suite-error"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={busy || !email || !password}
              className="w-full py-3 mt-2 bg-[#c9a96e] text-[#0d0c0a] font-mono text-[11px] uppercase tracking-[0.2em] rounded-md hover:bg-[#d9b87e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="jackson-suite-submit"
            >
              {busy ? "Entering…" : "Enter Suite"}
            </button>
          </form>

          <p className="mt-12 font-accent italic text-[12px] text-[#5a5346] leading-relaxed">
            Trouble signing in? Reply to Antonia's last note and she'll
            send a fresh access code.
          </p>
        </div>
      </main>

      <footer className="px-6 sm:px-12 pb-8">
        <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#3a342a]">
          Private · Encrypted · Member-only
        </p>
      </footer>
    </div>
  );
}
